/**
 * Component: Widget COUNTER Usage Tracking
 * SpecReferenceID: WIDGET-V1
 * Requirements: WIDGET-V1-R25
 *
 * Fire COUNTER usage events (``POST /api/v1/search/usage``)
 * for clinician actions in widget surfaces. Mirrors the
 * FRONTEND-WEB-V1 usage-tracking helper shape (same
 * action-type vocabulary, same payload fields) so the
 * widget's events are indistinguishable from main-app
 * events in the admin usage dashboard. This satisfies
 * R25's "COUNTER parity with FRONTEND-WEB-V1" acceptance
 * criterion.
 *
 * Design choices:
 *
 *   - Tracking calls never throw. A failed tracking
 *     request must not interrupt the clinician's
 *     workflow; we silently log and move on.
 *   - Auth goes through ``WidgetApiClient`` so the R06
 *     Bearer injection and R08 401 retry logic apply. The
 *     client already handles transport failures and 401 /
 *     429 / 5xx normalisation; we do not re-implement any
 *     of that.
 *   - Action types are a Literal union pinned against the
 *     FRONTEND-WEB-V1 ``UsageActionType`` type. Adding a
 *     new type here requires adding it on the frontend
 *     too (backend accepts both).
 */

import type { WidgetApiClient } from '../api/client';

/**
 * COUNTER action types the widget fires. Matches
 * ``src/frontend/src/lib/usage-tracking.ts`` so dashboard
 * consumers cannot tell whether an event originated in
 * the main app or the widget.
 */
export type WidgetUsageActionType =
  | 'link_open'
  | 'query_refinement'
  | 'result_expand'
  | 'copy_citation'
  | 'chat_query'
  | 'search_query'
  | 'citation_expand';

/**
 * Payload fields the COUNTER endpoint accepts. Every
 * field is optional; callers only populate the ones
 * relevant to their action.
 */
export interface WidgetUsageActionParams {
  document_id?: string;
  query_text?: string;
  previous_query?: string;
  ranking_position?: number;
  mode?: string;
  referrer_mode?: string;
  section_id?: number | null;
  guideline_name?: string;
  external_guideline_url?: string;
  section_name?: string;
  guideline_id?: string;
}

/**
 * Fire a COUNTER usage event. Silently swallows errors so
 * tracking cannot interrupt the clinician workflow even
 * if the backend is temporarily rate-limiting or down.
 *
 * ``referrer_mode`` is automatically tagged with
 * ``widget-<surface>`` so the admin dashboard can split
 * widget traffic from main-app traffic without needing a
 * separate event schema.
 */
export async function trackWidgetUsage(
  client: WidgetApiClient,
  surface: string,
  actionType: WidgetUsageActionType,
  params: WidgetUsageActionParams = {},
): Promise<void> {
  try {
    await client.postJson('/api/v1/search/usage', {
      action_type: actionType,
      referrer_mode: params.referrer_mode ?? `widget-${surface}`,
      ...params,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug('[GalenWidget] usage tracking failed:', actionType, e);
  }
}
